import React from "react";
import {
  UIStructuredOutput,
  Answer,
  ClarificationRequest,
  Refusal,
  Error as StructuredOutputError,
  AnswerElementsItem,
  Markdown,
  Table,
  LineGraph,
  UIStructuredOutputResponse,
} from "../../generated/core-api";
import { TableView } from "./TableView";
import { LineGraphView } from "./GraphViews";
import { MarkdownView } from "./MarkdownView";
import { CitationView } from "../../citations/components";
import styles from "./StructuredOutputView.module.css";

type DocumentElementViewProps = {
  element: AnswerElementsItem;
};

const DocumentElementView: React.FC<DocumentElementViewProps> = ({
  element,
}) => {
  switch (element.element_kind) {
    case "markdown":
      return <MarkdownView markdown={element as Markdown} />;
    case "table":
      return <TableView table={element as Table} />;
    case "linegraph":
      return <LineGraphView graph={element as LineGraph} />;
    default:
      return <div></div>;
  }
};

const DisclaimerView: React.FC = () => {
  return (
    <div className={styles.disclaimer}>
      <span className={styles.disclaimerIcon}>ℹ️</span>
      <span className={styles.disclaimerText}>
        This answer is based on general model knowledge and may be inaccurate.
        For better accuracy, please select knowledge sources from your
        organization's documents.
      </span>
    </div>
  );
};

type ClarificationViewProps = {
  clarification: ClarificationRequest;
};

const ClarificationView: React.FC<ClarificationViewProps> = ({
  clarification,
}) => {
  return (
    <div className={styles.clarification}>
      <div className={styles.clarificationHeader}>
        <span className={styles.clarificationIcon}>❓</span>
        <span className={styles.clarificationTitle}>Clarification Needed</span>
      </div>
      <div className={styles.clarificationQuestion}>
        {clarification.question}
      </div>
    </div>
  );
};

type RefusalViewProps = {
  refusal: Refusal;
};

const RefusalView: React.FC<RefusalViewProps> = ({ refusal }) => {
  return (
    <div className={styles.refusal}>
      <div className={styles.refusalHeader}>
        <span className={styles.refusalIcon}>⚠️</span>
        <span className={styles.refusalTitle}>Request Declined</span>
      </div>
      <div className={styles.refusalReason}>{refusal.reason}</div>
    </div>
  );
};

type ErrorViewProps = {
  error: StructuredOutputError;
};

const ErrorView: React.FC<ErrorViewProps> = ({ error }) => {
  return (
    <div className={styles.error}>
      <div className={styles.errorHeader}>
        <span className={styles.errorIcon}>❌</span>
        <span className={styles.errorTitle}>Error Occurred</span>
      </div>
      <div className={styles.errorReason}>{error.reason}</div>
    </div>
  );
};

type AnswerViewProps = {
  answer: Answer;
};

const AnswerView: React.FC<AnswerViewProps> = ({ answer }) => {
  const hasCitations = answer.citations && answer.citations.length > 0;

  return (
    <>
      {!hasCitations && <DisclaimerView />}
      {answer.elements.map((element: AnswerElementsItem, index: number) => (
        <div key={index} className={styles.element}>
          <DocumentElementView element={element} />
        </div>
      ))}
      {hasCitations && <CitationView citations={answer.citations!} />}
    </>
  );
};

type StructuredOutputViewProps = {
  data: UIStructuredOutput;
};

export const StructuredOutputView: React.FC<StructuredOutputViewProps> = ({
  data,
}) => {
  const isClarificationRequest = (
    response: UIStructuredOutputResponse
  ): response is ClarificationRequest => {
    return (
      "response_kind" in response &&
      response.response_kind === "clarification_request"
    );
  };

  const isRefusal = (
    response: UIStructuredOutputResponse
  ): response is Refusal => {
    return "response_kind" in response && response.response_kind === "refusal";
  };

  const isError = (
    response: UIStructuredOutputResponse
  ): response is StructuredOutputError => {
    return "response_kind" in response && response.response_kind === "error";
  };

  return (
    <div className={styles.container}>
      {isClarificationRequest(data.response) ? (
        <ClarificationView clarification={data.response} />
      ) : isRefusal(data.response) ? (
        <RefusalView refusal={data.response} />
      ) : isError(data.response) ? (
        <ErrorView error={data.response} />
      ) : (
        <AnswerView answer={data.response} />
      )}
    </div>
  );
};
